import { Box, Button, Flex, Heading, SimpleGrid, Stack, Tabs, Text, Table } from '@chakra-ui/react';
import { useCallback, useEffect, useState } from 'react';
import { apiGet } from '../api.js';
import CustomerLedgerReport from '../components/CustomerLedgerReport.jsx';

const formatDate = (value) => {
  if (!value) return '';
  const d = new Date(value);
  const day = String(d.getDate()).padStart(2, '0');
  const month = String(d.getMonth() + 1).padStart(2, '0');
  const year = d.getFullYear();
  return `${day}-${month}-${year}`;
};

const StatCard = ({ label, value, highlight }) => (
  <Box
    bg="white"
    borderWidth="1px"
    borderColor="gray.200"
    borderRadius="lg"
    p={4}
  >
    <Text fontSize="sm" color="gray.500">{label}</Text>
    <Heading size={highlight ? 'xl' : 'lg'} color={highlight ? 'green.600' : undefined}>
      {value}
    </Heading>
  </Box>
);

const VadlaItemsTable = ({ title, items, fmt }) => {
  if (!items?.length) return null;
  return (
    <Box mt={4}>
      <Text fontWeight="bold" mb={2}>{title}</Text>
      <Box overflowX="auto">
        <Table.Root size="sm" variant="line">
          <Table.Header>
            <Table.Row>
              <Table.ColumnHeader>No</Table.ColumnHeader>
              <Table.ColumnHeader>Item</Table.ColumnHeader>
              <Table.ColumnHeader>Client</Table.ColumnHeader>
              <Table.ColumnHeader>Transfer Date</Table.ColumnHeader>
              <Table.ColumnHeader textAlign="right">Principal</Table.ColumnHeader>
              <Table.ColumnHeader textAlign="right">Rate</Table.ColumnHeader>
              <Table.ColumnHeader textAlign="right">Interest</Table.ColumnHeader>
              <Table.ColumnHeader textAlign="right">Total</Table.ColumnHeader>
            </Table.Row>
          </Table.Header>
          <Table.Body>
            {items.map((row) => (
              <Table.Row key={row.entryId}>
                <Table.Cell>{row.transferNo || '-'}</Table.Cell>
                <Table.Cell>{row.articleName}</Table.Cell>
                <Table.Cell>{row.clientName}</Table.Cell>
                <Table.Cell>{formatDate(row.transferDate)}</Table.Cell>
                <Table.Cell textAlign="right">{fmt(row.principal)}</Table.Cell>
                <Table.Cell textAlign="right">{row.interestRate}%</Table.Cell>
                <Table.Cell textAlign="right">{fmt(row.interestTillToday)}</Table.Cell>
                <Table.Cell textAlign="right" fontWeight="bold">{fmt(row.totalTillToday)}</Table.Cell>
              </Table.Row>
            ))}
          </Table.Body>
        </Table.Root>
      </Box>
    </Box>
  );
};

const SummaryReport = () => {
  const [data, setData] = useState(null);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(true);
  const [selectedVadlaId, setSelectedVadlaId] = useState('');

  const fetchReports = useCallback(async (vadlaUserId = selectedVadlaId) => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (vadlaUserId) params.set('vadlaUserId', vadlaUserId);
      const query = params.toString();
      const result = await apiGet(`/reports${query ? `?${query}` : ''}`);
      setData(result);
      setError('');
    } catch (err) {
      setError(err.message || 'Failed to load reports');
    } finally {
      setLoading(false);
    }
  }, [selectedVadlaId]);

  useEffect(() => {
    fetchReports();
    const refresh = () => fetchReports();
    window.addEventListener('focus', refresh);
    return () => window.removeEventListener('focus', refresh);
  }, [fetchReports]);

  const handleVadlaChange = (e) => {
    const id = e.target.value;
    setSelectedVadlaId(id);
    fetchReports(id);
  };

  const fmt = (n) => `₹${Number(n || 0).toLocaleString('en-IN', { maximumFractionDigits: 2 })}`;
  const detail = data?.vadlaPartyDetail;
  const parties = data?.vadlaParties || [];

  return (
    <Stack spacing={6}>
      <Flex justify="space-between" align="center" wrap="wrap" gap={2}>
        <Box>
          <Heading size="md">Summary</Heading>
          <Text color="gray.500" fontSize="sm">
            Till today
            {data?.asOf ? ` (as of ${formatDate(data.asOf)})` : ''}
          </Text>
        </Box>
        <Button size="sm" variant="outline" onClick={() => fetchReports()} loading={loading}>
          Refresh
        </Button>
      </Flex>

      {error && <Text color="red.500">{error}</Text>}

      {loading && !data && <Text color="gray.500">Loading reports...</Text>}

      {data && (
        <>
          <Box>
            <Heading size="md" mb={4}>Our Articles (Girvi)</Heading>
            <SimpleGrid columns={{ base: 1, sm: 2, lg: 3 }} gap={4}>
              <StatCard label="Total articles" value={data.articles.totalCount} />
              <StatCard label="Active articles" value={data.articles.activeCount} />
              <StatCard label="Released articles" value={data.articles.releasedCount} />
              <StatCard label="Total principal (active)" value={fmt(data.articles.totalPrincipal)} highlight />
              <StatCard
                label="Pending interest till today"
                value={fmt(data.articles.totalPendingInterestTillToday ?? data.articles.totalInterestTillToday)}
                highlight
              />
              <StatCard
                label="Total interest paid till today"
                value={fmt(data.articles.totalInterestPaidTillToday)}
                highlight
              />
            </SimpleGrid>
            <Text fontSize="xs" color="gray.500" mt={2}>
              First 30 days always charge 1 month — even if released on day 1 or day 20. After 30 days, interest is daily. A 31-day month counts as 30 days. Principal paid reduces daily interest from the next day.
            </Text>
          </Box>

          <Box>
            <Heading size="md" mb={4}>Vadla (All parties)</Heading>
            <SimpleGrid columns={{ base: 1, sm: 2, lg: 3 }} gap={4} mb={4}>
              <StatCard label="Items with vadla (active)" value={data.vadla.activeCount} />
              <StatCard label="Released from vadla" value={data.vadla.releasedCount} />
              <StatCard label="Total vadla principal" value={fmt(data.vadla.totalPrincipal)} highlight />
              <StatCard label="Total vadla interest till today" value={fmt(data.vadla.totalInterestTillToday)} highlight />
            </SimpleGrid>
          </Box>

          <Box p={4} borderWidth="1px" borderRadius="lg" bg="white">
            <Heading size="md" mb={3}>Vadla party-wise report</Heading>
            <Text fontSize="sm" color="gray.600" mb={3}>
              Select a vadla party to see item-wise principal and interest.
            </Text>
            <Box mb={4} maxW="400px">
              <Text fontSize="sm" fontWeight="bold" mb={1}>Select Vadla Party</Text>
              <select
                value={selectedVadlaId}
                onChange={handleVadlaChange}
                style={{ width: '100%', padding: '8px', borderRadius: '6px', border: '1px solid #e2e8f0' }}
              >
                <option value="">-- Select Vadla Party --</option>
                {parties.map((p) => (
                  <option key={p._id} value={p._id}>
                    {p.name}
                    {p.no ? ` (${p.no})` : ''}
                    {p.mobile ? ` · ${p.mobile}` : ''}
                    {' — '}
                    {p.activeCount} active · {fmt(p.totalPrincipal)}
                  </option>
                ))}
              </select>
            </Box>

            {parties.length > 0 && !selectedVadlaId && (
              <Box overflowX="auto" mb={4}>
                <Text fontWeight="bold" mb={2}>All parties summary</Text>
                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.85rem' }}>
                  <thead>
                    <tr>
                      <th style={{ textAlign: 'left', padding: '8px', borderBottom: '1px solid #e2e8f0' }}>Party</th>
                      <th style={{ textAlign: 'left', padding: '8px', borderBottom: '1px solid #e2e8f0' }}>No</th>
                      <th style={{ textAlign: 'right', padding: '8px', borderBottom: '1px solid #e2e8f0' }}>Active</th>
                      <th style={{ textAlign: 'right', padding: '8px', borderBottom: '1px solid #e2e8f0' }}>Released</th>
                      <th style={{ textAlign: 'right', padding: '8px', borderBottom: '1px solid #e2e8f0' }}>Principal</th>
                      <th style={{ textAlign: 'right', padding: '8px', borderBottom: '1px solid #e2e8f0' }}>Interest</th>
                    </tr>
                  </thead>
                  <tbody>
                    {parties.map((p) => (
                      <tr
                        key={p._id}
                        style={{ cursor: 'pointer' }}
                        onClick={() => {
                          setSelectedVadlaId(p._id);
                          fetchReports(p._id);
                        }}
                      >
                        <td style={{ padding: '8px', borderBottom: '1px solid #edf2f7' }}>{p.name}</td>
                        <td style={{ padding: '8px', borderBottom: '1px solid #edf2f7' }}>{p.no || '-'}</td>
                        <td style={{ padding: '8px', borderBottom: '1px solid #edf2f7', textAlign: 'right' }}>{p.activeCount}</td>
                        <td style={{ padding: '8px', borderBottom: '1px solid #edf2f7', textAlign: 'right' }}>{p.releasedCount}</td>
                        <td style={{ padding: '8px', borderBottom: '1px solid #edf2f7', textAlign: 'right' }}>{fmt(p.totalPrincipal)}</td>
                        <td style={{ padding: '8px', borderBottom: '1px solid #edf2f7', textAlign: 'right' }}>{fmt(p.totalInterestTillToday)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </Box>
            )}

            {detail && (
              <Box mt={4} p={4} bg="gray.50" borderRadius="md">
                <Text fontSize="lg" fontWeight="bold">
                  {detail.party.name}
                  {detail.party.no ? ` · ${detail.party.no}` : ''}
                  {detail.party.mobile ? ` · ${detail.party.mobile}` : ''}
                </Text>
                <SimpleGrid columns={{ base: 1, sm: 2, lg: 4 }} gap={3} mt={3} mb={2}>
                  <StatCard label="Active items" value={detail.summary.activeCount} />
                  <StatCard label="Released items" value={detail.summary.releasedCount} />
                  <StatCard label="Principal (active)" value={fmt(detail.summary.totalPrincipal)} highlight />
                  <StatCard label="Interest till today" value={fmt(detail.summary.totalInterestTillToday)} highlight />
                </SimpleGrid>
                <VadlaItemsTable title="Active items" items={detail.activeItems} fmt={fmt} />
                <VadlaItemsTable title="Released items" items={detail.releasedItems} fmt={fmt} />
              </Box>
            )}

            {selectedVadlaId && !detail && !loading && (
              <Text color="gray.500" fontSize="sm">No data for selected party.</Text>
            )}
          </Box>
        </>
      )}
    </Stack>
  );
};

const Reports = () => {
  return (
    <Box>
      <Heading size="lg" mb={4}>Reports</Heading>
      <Tabs.Root defaultValue="summary">
        <Tabs.List>
          <Tabs.Trigger value="summary">Summary</Tabs.Trigger>
          <Tabs.Trigger value="ledger">Customer Ledger</Tabs.Trigger>
        </Tabs.List>
        <Tabs.Content value="summary" pt={4}>
          <SummaryReport />
        </Tabs.Content>
        <Tabs.Content value="ledger" pt={4}>
          <CustomerLedgerReport />
        </Tabs.Content>
      </Tabs.Root>
    </Box>
  );
};

export default Reports;
